import { userHasActiveEntitlement } from "./entitlements";

const EXPECTED_PRICING_FEATURE_KEYS = {
  guest: [
    "guest_recipe_search",
    "guest_recipe_view_details",
    "guest_mealplan_3_days_month",
    "guest_shopping_preview",
    "guest_favorites_locked",
    "guest_lists_locked",
    "guest_inventory_locked",
    "guest_recipe_create_locked",
  ],
  free: [
    "free_favorites_enabled",
    "free_mealplan_14_days_per_plan",
    "free_mealplan_save_14_days_month",
    "free_lists_3_with_60_items",
    "free_inventory_30_items",
    "free_private_recipes_only",
  ],
  premium: [
    "premium_favorites_enabled",
    "premium_mealplan_unlimited",
    "premium_lists_unlimited",
    "premium_inventory_unlimited",
    "premium_expiry_email_alerts",
    "premium_price_history_365_days",
    "premium_followed_products",
    "premium_unlimited_price_alerts",
    "premium_daily_digest_email",
    "premium_recipe_pending_visibility",
  ],
};

const PRICING_FEATURES = {
  guest: EXPECTED_PRICING_FEATURE_KEYS.guest.map((key) => ({ key, yes: !key.endsWith("_locked") })),
  free: EXPECTED_PRICING_FEATURE_KEYS.free.map((key) => ({ key, yes: true })),
  premium: EXPECTED_PRICING_FEATURE_KEYS.premium.map((key) => ({ key, yes: true })),
};

function assertPricingFeatureMatrix() {
  if (process.env.NODE_ENV === "production") return;

  const mismatch = Object.entries(EXPECTED_PRICING_FEATURE_KEYS).find(([tier, expectedKeys]) => {
    const actualKeys = (PRICING_FEATURES[tier] || []).map((item) => item.key);
    return JSON.stringify(actualKeys) !== JSON.stringify(expectedKeys);
  });

  if (mismatch) {
    const [tier, expectedKeys] = mismatch;
    const actualKeys = (PRICING_FEATURES[tier] || []).map((item) => item.key);
    // eslint-disable-next-line no-console
    console.error("[pricing] Feature matrix drift detected", { tier, expectedKeys, actualKeys });
  }
}

assertPricingFeatureMatrix();

export function getTier(user, entitlements = null) {
  const effectiveEntitlements = entitlements || user?.entitlements || null;
  if (userHasActiveEntitlement(user) || effectiveEntitlements?.has_access === true || effectiveEntitlements?.tier === "premium") {
    return "premium";
  }
  if (user || effectiveEntitlements?.tier === "free") {
    return "free";
  }
  return "guest";
}

export function getCapabilities({ user = null, entitlements = null } = {}) {
  const tier = getTier(user, entitlements);
  const isLoggedIn = tier !== "guest";
  const isPremium = tier === "premium";

  return {
    tier,
    caps: {
      recipeSearch: true,
      recipeDetails: true,
      favorites: {
        available: isLoggedIn,
      },
      shoppingLists: {
        available: isLoggedIn,
        maxLists: tier === "guest" ? 0 : tier === "free" ? 3 : null,
        maxItemsPerList: tier === "guest" ? 0 : tier === "free" ? 60 : null,
      },
      inventory: {
        available: isLoggedIn,
        maxItems: tier === "guest" ? 0 : tier === "free" ? 30 : null,
      },
      mealPlan: {
        available: true,
        maxDaysPerPlan: tier === "guest" ? 3 : tier === "free" ? 14 : null,
        monthlySavedDays: tier === "guest" ? 3 : tier === "free" ? 14 : null,
      },
      priceWatch: {
        available: isPremium,
      },
      priceHistory: {
        available: isPremium,
        maxDays: isPremium ? 365 : 0,
      },
      priceAlerts: {
        available: isPremium,
        maxAlerts: isPremium ? null : 0,
      },
      recipeCreation: {
        available: isLoggedIn,
        visibility: tier === "guest" ? "none" : tier === "premium" ? "private_or_pending" : "private_only",
      },
      expiryAlerts: {
        available: isPremium,
      },
      dailyDigestEmail: {
        available: isPremium,
      },
      pricing: PRICING_FEATURES,
    },
  };
}
